import { z } from "zod";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — modul Dokumenty (soubory na firemním Google Drive).
 *
 * Soubory žijí výhradně na Drive („HCA ERP" + podsložky podle klienta či
 * kategorie), v DB jsou jen metadata. Schémata sdílí server (tRPC + upload
 * routa) a web (validace před odesláním).
 */

/** Maximální velikost nahrávaného souboru: 15 MB. */
export const DOKUMENT_MAX_BYTES = 15 * 1024 * 1024;

/** Povolené typy souborů: PDF, PNG, JPG, DOCX. */
export const DOKUMENT_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

export type DokumentMimeType = (typeof DOKUMENT_ALLOWED_MIME_TYPES)[number];

/** True pro mime typ, který upload přijme. */
export function isAllowedDokumentMime(mime: string): mime is DokumentMimeType {
  return (DOKUMENT_ALLOWED_MIME_TYPES as readonly string[]).includes(mime);
}

/** Návrhy kategorií pro upload (datalist) — volný text, tohle jsou jen tipy. */
export const DOKUMENT_KATEGORIE_NAVRHY = [
  "Materiály k seminářům",
  "Smlouvy",
  "Faktury podklady",
] as const;

const uuidMsg = "Neplatné UUID";

const nazevField = z
  .string()
  .trim()
  .min(1, "Název je povinný")
  .max(200, "Název musí být 200 znaků nebo méně");

const kategorieField = z
  .string()
  .trim()
  .min(1, "Kategorie nesmí být prázdná")
  .max(100, "Kategorie musí být 100 znaků nebo méně");

/** Whitelist řazení dokumentů — router mapuje klíče na sloupce. */
export const DOKUMENTY_SORT_KEYS = ["nazev", "velikost", "klient", "kategorie", "nahrano"] as const;
export type DokumentySortKey = (typeof DOKUMENTY_SORT_KEYS)[number];

/** Seznam dokumentů — stránkování + vyhledávání dle názvu + filtry vazeb. */
export const listDokumentySchema = paginationSchema.merge(sortSchema(DOKUMENTY_SORT_KEYS)).extend({
  search: z.string().trim().max(255, "Hledaný výraz musí být 255 znaků nebo méně").optional(),
  klientId: z.string().uuid(uuidMsg).optional(),
  kategorie: kategorieField.optional(),
});

export type ListDokumentyInput = z.infer<typeof listDokumentySchema>;

/** Přejmenování dokumentu (propíše se i na Drive). */
export const renameDokumentSchema = z.object({
  id: z.string().uuid(uuidMsg),
  nazev: nazevField,
});

export type RenameDokumentInput = z.infer<typeof renameDokumentSchema>;

/** Smazání dokumentu (soft delete v DB + smazání z Drive). */
export const removeDokumentSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

export type RemoveDokumentInput = z.infer<typeof removeDokumentSchema>;

/**
 * Textová pole multipart uploadu (soubor samotný validuje routa zvlášť).
 * `klientId` a `kategorie` se navzájem vylučují — klient má přednost,
 * hlídá to `.refine`. Prázdné stringy z formuláře mapujeme na `undefined`.
 */
export const uploadDokumentFieldsSchema = z
  .object({
    nazev: nazevField.optional(),
    klientId: z
      .string()
      .trim()
      .transform((v) => (v.length === 0 ? undefined : v))
      .pipe(z.string().uuid(uuidMsg).optional()),
    kategorie: z
      .string()
      .trim()
      .max(100, "Kategorie musí být 100 znaků nebo méně")
      .transform((v) => (v.length === 0 ? undefined : v)),
  })
  .refine((v) => !(v.klientId && v.kategorie), {
    message: "Vyberte klienta NEBO kategorii, ne obojí",
    path: ["kategorie"],
  });

export type UploadDokumentFieldsInput = z.infer<typeof uploadDokumentFieldsSchema>;

/**
 * Tvar ID složky na Google Drive — část adresy za `/folders/`. Sdílené disky
 * mívají ID začínající „0A", jinak jde o base64url-like řetězec.
 */
const driveFolderIdPattern = /^[A-Za-z0-9_-]{5,200}$/;

/**
 * Nastavení nadřazené složky, POD kterou se zakládá kořen „HCA ERP"
 * (typicky složka „HCA MORAVIA" na sdíleném disku). Prázdný vstup = Můj
 * disk připojeného účtu (null). Když admin vloží celou adresu z prohlížeče,
 * ID se z ní vytáhne automaticky.
 */
export const setDriveParentFolderSchema = z.object({
  parentFolderId: z
    .string()
    .trim()
    .max(1000, "ID složky je příliš dlouhé")
    .transform((value) => {
      const match = value.match(/\/folders\/([A-Za-z0-9_-]+)/);
      const id = match?.[1] ?? value;
      return id.length === 0 ? null : id;
    })
    .pipe(
      z
        .string()
        .max(200, "ID složky musí být 200 znaků nebo méně")
        .regex(driveFolderIdPattern, "Neplatné ID složky — zkopírujte část adresy za /folders/")
        .nullable(),
    ),
});

export type SetDriveParentFolderInput = z.infer<typeof setDriveParentFolderSchema>;

/** Callback z Google OAuth pro Drive — stejný tvar jako u synchronizace kontaktů. */
export const googleDriveCallbackSchema = z.object({
  code: z.string().min(1).max(2048).optional(),
  state: z.string().min(1).max(2048).optional(),
  error: z.string().max(255).optional(),
});

export type GoogleDriveCallbackInput = z.infer<typeof googleDriveCallbackSchema>;

/** Metadata dokumentu, jak je vrací API (serializovaný řádek + jména vazeb). */
export interface HcaDokument {
  id: string;
  nazev: string;
  driveFileId: string;
  mimeType: string;
  velikostBytes: number | null;
  odkaz: string;
  klientId: string | null;
  /** „Jméno Příjmení" klienta pro sloupec v tabulce; null bez vazby. */
  klientJmeno: string | null;
  kategorie: string | null;
  nahralId: string | null;
  nahralJmeno: string | null;
  createdAt: string;
}
